import {
  Box,
  Column,
  Columns,
  ContentBlock,
  Heading,
  List,
  Strong,
  Text,
  TextLink,
} from 'braid-design-system';
import type { DataNode, Element, Node } from 'domhandler';
import { convertNodeToElement, type Transform } from 'react-html-parser';

import { binary } from '../helpers';

import * as styles from './Table.css';

export const transformToBraid: Transform = (
  node: Element | DataNode,
  index,
): React.ReactElement | null => {
  switch (node.type) {
    case 'text':
      // Render the default value.
      return convertNodeToElement(node, index, transformToBraid);
    case 'tag':
      // This is likely rendered in a loop so we add a key to each item.
      return withKey(mapTagToBraid(node as Element, index), index);
    default:
      // Only render the types we have implemented.
      return null;
  }
};

function withKey(
  Element: React.FunctionComponent | null,
  key: string | number,
) {
  return Element ? <Element key={key} /> : Element;
}

/**
 * Maps defined tag to a braid component or html fallback.
 *
 * ReactElement - renders the element returned.
 * void - renders the default element html (e.g., <br/>, <code>, etc.)
 * null - Does not render the element or its children.
 */
function mapTagToBraid(
  node: Element,
  index: number,
): React.FunctionComponent | null {
  const { name, children, attribs } = node;
  switch (name) {
    case 'div':
    case 'tbody':
      return () => <Box {...attribs}>{renderChildren(children) as any}</Box>;
    case 'h1':
      return () => (
        <Box paddingTop="medium">
          <Heading level="1">{renderChildren(children) as any}</Heading>
        </Box>
      );
    case 'h2':
      return () => (
        <Box paddingTop="medium">
          <Heading level="2">{renderChildren(children) as any}</Heading>
        </Box>
      );
    case 'h3':
      return () => (
        <Heading level="3">{renderChildren(children) as any}</Heading>
      );
    case 'h4':
      return () => (
        <Heading level="4">{renderChildren(children) as any}</Heading>
      );
    case 'p':
      return () => <Text component="p">{renderChildren(children) as any}</Text>;
    case 'strong':
      return () => <Strong>{renderChildren(children) as any}</Strong>;
    case 'a':
      return () => (
        <TextLink {...attribs} href={attribs.href}>
          {renderChildren(children) as any}
        </TextLink>
      );
    case 'table':
      return () => (
        <Box
          boxShadow="borderNeutralLight"
          borderRadius="standard"
          padding="xsmall"
        >
          <ContentBlock>{renderChildren(children) as any}</ContentBlock>
        </Box>
      );
    case 'th':
    case 'tr':
      return () => (
        <Box className={styles.tr}>
          <Columns space="gutter" collapseBelow="tablet">
            {renderChildren(children) as any}
          </Columns>
        </Box>
      );
    case 'td':
      return () => <Column>{renderChildren(children) as any}</Column>;
    case 'ul':
      return () => <List type="bullet">{renderChildren(children) as any}</List>;
    case 'ol':
      return () => <List type="number">{renderChildren(children) as any}</List>;
    case 'li':
      return () => <Text>{renderChildren(children) as any}</Text>;
    /**
     * We don't have or don't need Braid components for these so
     * we will fall back to the generic html elements.
     */
    case 'br':
    case 'u':
    case 'em':
    case 'br':
    case 'code':
      return () => convertNodeToElement(node, index, transformToBraid);
    default:
      // If there is no match we don't want to break the app,
      // instead, throw an error and keep rendering.
      // eslint-disable-next-line no-console
      console.error(`No match for element named '${String(name)}'`);
      return null;
  }
}

const renderChildren = (children: Node[] | undefined) =>
  children?.map(binary(transformToBraid));
